import { ResolveFn } from "@angular/router";
import { AuthenticationToken } from "../models/authentication-token";
import { inject } from "@angular/core";
import { AuthenticationService } from "../services/authentication.service";
import { NavigationSource } from "../../application/enums/navigation-source";
import { IAPI_APP_ROUTER_SERVICE } from "../constants/iapi-app-tokens";

/**
 * Used to resolve a temporary token from the route query param
 *
 * Is used on the forgot-password and accept-invitation routes
 *
 * If the token is invalid it will redirect to the login page with an appropriate message
 *
 * If the token is expired it will re-request a new token email and will redirect to the login page with an appropriate message
 *
 * Example Route
 * ```
 * {
 *    ...
 *    resolvers: {
 *      temporaryToken: temporaryTokenResolver
 *    }
 * }
 * ```
 * @private
 */
export const temporaryTokenResolver: ResolveFn<AuthenticationToken> = (route) => {

    const jwtToken: string = route.queryParams?.token;
    const authService = inject(AuthenticationService);
    const appRouter = inject(IAPI_APP_ROUTER_SERVICE);
    const errorRedirect = appRouter.getLoginRedirectCommand(NavigationSource.Resolver);
    if (jwtToken) {
        let token: AuthenticationToken;

        try {
            token = new AuthenticationToken(jwtToken);
        }
        catch (error) {
            authService.handleInvalidTemporaryToken();
            return errorRedirect;
        }
        if (token.isExpired) {
            authService.handleExpiredTemporaryToken(token);
            return errorRedirect;
        }
        return token;
    }
    else {
        authService.handleInvalidTemporaryToken();
        return errorRedirect;
    }
};
